import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Crown, Award, Check } from "lucide-react";
import CrownFellowCelebration from "@/components/CrownFellowCelebration";

const LEVELS = [
  { name: "Crown Candidate", min: 0 },
  { name: "Crown Scholar", min: 2 },
  { name: "Crown Mentor", min: 4 },
  { name: "Crown Ambassador", min: 6 },
  { name: "Crown Fellow", min: 7 },
];

export default function Certificate() {
  const [cert, setCert] = useState(null);
  const ref = useRef();

  useEffect(() => {
    api.get("/certificate").then((r) => setCert(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progressData = useMemo(() => {
    if (!cert) return null;
    const done = cert.completed_tracks.length;
    const total = cert.total_tracks || 7;
    const pct = Math.min(100, Math.round((done / total) * 100));
    const currentIdx = LEVELS.findIndex((l) => l.name === cert.level);
    const nextLevel = !cert.all_complete && currentIdx >= 0 && currentIdx < LEVELS.length - 1
      ? LEVELS[currentIdx + 1]
      : null;
    const tracksToNext = nextLevel ? Math.max(0, nextLevel.min - done) : 0;
    return { done, total, pct, nextLevel, tracksToNext };
  }, [cert]);

  if (!cert)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#8A8782]">
        Loading…
      </div>
    );

  return (
    <div className="bg-tcpi-bg min-h-screen py-12 px-6" data-testid="certificate-page">
      <CrownFellowCelebration userId={cert.user_id} enabled={cert.all_complete} />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="overline mb-3">Certification</div>
          <h1
            className="text-4xl md:text-5xl text-[#F7F5F0]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Your Crown Path Status
          </h1>
        </div>

        {/* Progress-to-Fellow banner (always visible) */}
        <div
          className="mb-8 rounded-lg border border-[#C9A961]/40 bg-[#1A1816] p-5"
          data-testid="path-progress-banner"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <div className="overline text-[10px] mb-1">Path to Crown Fellow</div>
              <div className="text-[#F7F5F0]">
                <span
                  className="text-3xl text-[#C9A961] mr-1"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                  data-testid="path-progress-count"
                >
                  {progressData.done}
                </span>
                <span className="text-[#8A8782]">of {progressData.total} tracks completed</span>
              </div>
            </div>
            <div className="text-right">
              <PathProgressHint
                allComplete={cert.all_complete}
                nextLevel={progressData.nextLevel}
                tracksToNext={progressData.tracksToNext}
              />
            </div>
          </div>
          <div
            className="h-2 bg-[#0A0908] rounded-full overflow-hidden border border-[#C9A961]/15"
            data-testid="path-progress-bar"
          >
            <div
              className="h-full bg-[#C9A961] transition-all duration-700"
              style={{ width: `${progressData.pct}%` }}
            />
          </div>
        </div>

        {/* Certificate card — LIGHT theme */}
        <div
          ref={ref}
          className="relative bg-[#FAF6EC] border-[3px] border-[#8C6A1A] rounded-lg p-10 md:p-14 text-center overflow-hidden"
          style={{
            boxShadow: "0 10px 40px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(140,106,26,0.12)",
          }}
          data-testid="certificate-card"
        >
          <div className="absolute inset-4 border border-[#8C6A1A]/35 rounded pointer-events-none" />
          <div className="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-[#8C6A1A]/70 rounded-tl" />
          <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-[#8C6A1A]/70 rounded-tr" />
          <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-[#8C6A1A]/70 rounded-bl" />
          <div className="absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 border-[#8C6A1A]/70 rounded-br" />

          <Crown className="w-20 h-20 mx-auto text-[#8C6A1A] mb-6" strokeWidth={1.2} />

          <div
            className="overline mb-6"
            style={{ color: "#8C6A1A", letterSpacing: "0.18em" }}
          >
            The Crown Path Initiative
          </div>

          <div
            className="text-2xl md:text-3xl text-[#3a3833] mb-3"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            This is to certify that
          </div>

          <div
            className="text-4xl md:text-6xl text-[#8C6A1A] mb-5 italic"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
            data-testid="cert-name"
          >
            {cert.user_name}
          </div>

          <div
            className="text-xl text-[#3a3833] mb-4"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            has earned the level of
          </div>

          <div
            className="text-3xl md:text-5xl text-[#1E1B16] mb-8"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
            data-testid="cert-level"
          >
            {cert.level}
          </div>

          <div
            className="h-px w-32 mx-auto mb-6"
            style={{ background: "linear-gradient(90deg, transparent, #8C6A1A, transparent)" }}
          />

          <div className="text-[#3a3833] mb-2 text-base">
            <span className="font-semibold text-[#8C6A1A]">
              {cert.completed_tracks.length}
            </span>{" "}
            of {cert.total_tracks} tracks completed
          </div>
          <div
            className="text-[10px] uppercase tracking-[0.18em]"
            style={{ color: "#8C6A1A" }}
          >
            Issued {new Date(cert.issued_on).toLocaleDateString()}
          </div>
          <div className="text-[10px] text-[#6b6862] mt-3 italic">
            Issued in your legal name for verifiable credit, regardless of any community alias.
          </div>

          {cert.all_complete && (
            <div
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 border border-[#8C6A1A] rounded-full bg-[#8C6A1A]/8"
              data-testid="fellow-badge"
            >
              <Award className="w-5 h-5 text-[#8C6A1A]" />
              <span className="text-[#8C6A1A] font-medium">
                Crown Fellow — All Tracks Complete
              </span>
            </div>
          )}

          <div
            className="mt-10 italic text-[#6b6862]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            "The crown is not given. It is earned."
          </div>
        </div>

        <div className="text-center mt-8 flex gap-3 justify-center flex-wrap">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/certificate/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="download-cert-pdf-btn"
          >
            Download PDF
          </a>
          <button
            onClick={() => window.print()}
            className="btn-secondary"
            data-testid="print-cert-btn"
          >
            Print Certificate
          </button>
        </div>

        {/* Level ladder */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
          {LEVELS.map((lvl) => {
            const reached = progressData.done >= lvl.min;
            const active = lvl.name === cert.level;
            let cardStateClass;
            if (active) cardStateClass = "border-[#C9A961] bg-[#C9A961]/15 shadow-[0_0_22px_rgba(201,169,97,0.25)]";
            else if (reached) cardStateClass = "border-[#C9A961]/45 bg-[#C9A961]/5";
            else cardStateClass = "border-[#C9A961]/15";
            return (
              <div
                key={lvl.name}
                className={`p-4 rounded-md border transition ${cardStateClass}`}
                data-testid={`level-${lvl.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Crown
                  className={`w-5 h-5 mx-auto mb-1 ${
                    reached ? "text-[#C9A961]" : "text-[#8A8782]"
                  }`}
                  strokeWidth={1.4}
                />
                <div
                  className={`text-xs ${
                    reached ? "text-[#C9A961]" : "text-[#8A8782]"
                  }`}
                >
                  {lvl.name}
                </div>
                <div
                  className={`text-[10px] mt-1 ${
                    reached ? "text-[#C9A961]/80" : "text-[#8A8782]/70"
                  }`}
                >
                  {lvl.min === 7 ? "All 7 tracks" : `${lvl.min}+ tracks`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PathProgressHint({ allComplete, nextLevel, tracksToNext }) {
  if (allComplete) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C9A961]/15 text-[#C9A961] text-xs tracking-wide">
        <Check className="w-3.5 h-3.5" /> Full Fellowship reached
      </span>
    );
  }
  if (!nextLevel) return null;
  return (
    <span className="text-xs text-[#D1CFC9]" data-testid="next-level-hint">
      {tracksToNext} more track{tracksToNext === 1 ? "" : "s"} to{" "}
      <span className="text-[#C9A961]">{nextLevel.name}</span>
    </span>
  );
}

